/**
 * Report Profiles Admin Page
 * Route: /report-profiles
 *
 * Lists all report profiles and lets admins create/edit/delete them.
 * Integrates the ReportBlockBuilder for composing block configurations.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useOutletContext } from "react-router-dom";
import { buildReportProfilesContext } from "./assistantPageContext";
import toast from "react-hot-toast";
import ReportBlockBuilder from "./ReportBlockBuilder";
import ProfileBuilderAssist from "./ProfileBuilderAssist";
import ProfileAssignmentForm, {
  assignmentPrefsToPlanPatch,
  defaultAssignmentPrefs,
} from "./ProfileAssignmentForm";
import {
  assignProfileBulk,
  createProfile,
  deleteProfile,
  fetchAssignmentCompanies,
  fetchBlockCatalog,
  fetchProfiles,
  openModularReportPdf,
  updateProfile,
} from "../api/reportProfilesClient";
import { isAbortError } from "../utils/fetchAbort";
import {
  COMPANY_PROFILE_PRESETS,
  assessmentFormatSectionLabels,
  defaultBlocksForBase,
  filterCatalogForProfile,
  isFiveXAssessmentFormat,
  filterProfilesForWorkspace,
  isTeamReportBase,
  normalizeDraftBlocks,
  getRolesForFormat,
  PROFILE_ROLES,
} from "./reportProfileDefaults";
import { fetchAssessmentFormats } from "../api/adminClient";

// Legacy constant kept for callers that don't have the format context yet.
// Profile editor uses getRolesForFormat(selectedFormat) instead.
const ROLES = PROFILE_ROLES;

function Spinner({ className = "h-5 w-5" }) {
  return (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

function DefaultBadge() {
  return (
    <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium tracking-wide uppercase">
      Default
    </span>
  );
}

function RoleBadge({ role }) {
  if (!role) return null;
  return (
    <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">
      {role}
    </span>
  );
}

const VIEW_MODE_STORAGE_KEY = "report-profiles-view-mode";

function ViewModeToggle({ value, onChange }) {
  return (
    <div
      className="inline-flex rounded-xl border border-slate-200/80 bg-slate-50/80 p-1 shrink-0"
      role="group"
      aria-label="Layout"
    >
      <button
        type="button"
        title="List view"
        aria-label="List view"
        aria-pressed={value === "list"}
        className={`p-2 rounded-lg transition-all ${
          value === "list"
            ? "bg-white text-[#004F73] shadow-sm ring-1 ring-slate-200/80"
            : "text-slate-500 hover:text-slate-800"
        }`}
        onClick={() => onChange("list")}
      >
        <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4" aria-hidden>
          <path
            fillRule="evenodd"
            d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      <button
        type="button"
        title="Tiles view"
        aria-label="Tiles view"
        aria-pressed={value === "tiles"}
        className={`p-2 rounded-lg transition-all ${
          value === "tiles"
            ? "bg-white text-[#004F73] shadow-sm ring-1 ring-slate-200/80"
            : "text-slate-500 hover:text-slate-800"
        }`}
        onClick={() => onChange("tiles")}
      >
        <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4" aria-hidden>
          <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
      </button>
    </div>
  );
}

function ReportBaseToggle({ value, onChange }) {
  return (
    <div
      className="inline-flex rounded-xl border border-slate-200/80 bg-slate-50/80 p-1 shrink-0"
      role="tablist"
      aria-label="Report type"
    >
      <button
        type="button"
        role="tab"
        aria-selected={value === "company"}
        className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
          value === "company"
            ? "bg-white text-[#004F73] shadow-sm ring-1 ring-slate-200/80"
            : "text-slate-600 hover:text-slate-900"
        }`}
        onClick={() => onChange("company")}
      >
        Company
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={value === "team"}
        className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
          value === "team"
            ? "bg-white text-[#004F73] shadow-sm ring-1 ring-slate-200/80"
            : "text-slate-600 hover:text-slate-900"
        }`}
        onClick={() => onChange("team")}
      >
        Team
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PDF Page Preview — mirrors real advancereport.html / companyreport.html
// Each block maps to the exact page-break sections in the real templates.
// ---------------------------------------------------------------------------

const ROW_ODD  = "#D3D3D3";
const ROW_EVEN = "#F0F0F0";
const TH_BG    = "#004f73";
const TH_COLOR = "#fff";

function ReportTable({ headers, rows }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 8 }}>
      <thead>
        <tr>
          {headers.map((h, i) => (
            <th
              key={i}
              style={{
                background: TH_BG,
                color: TH_COLOR,
                padding: "3px 4px",
                textAlign: i === 0 ? "center" : i === headers.length - 1 ? "center" : "left",
                fontWeight: 600,
                border: "1px solid #fff",
              }}
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, ri) => (
          <tr key={ri} style={{ background: ri % 2 === 0 ? ROW_ODD : ROW_EVEN }}>
            {row.map((cell, ci) => (
              <td
                key={ci}
                style={{
                  padding: "2px 4px",
                  textAlign: ci === 0 ? "center" : ci === row.length - 1 ? "center" : "left",
                  color: "#333",
                  fontSize: 8,
                }}
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

function PillarRankingTable({ pillar, rows }) {
  return (
    <div>
      <p style={{ fontSize: 7, color: "#333", marginBottom: 4 }}>
        The following statements are ranked based on their performance score. Performance % = (Avg. Importance × Avg. Agreement) / (Avg. Importance × 5) × 100
      </p>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 7 }}>
        <thead>
          <tr>
            {["No.", "Statement", "Avg Imp", "Avg Agr", "Avg Impr Opp", "Performance"].map((h, i) => (
              <th key={i} style={{ background: TH_BG, color: TH_COLOR, padding: "2px 3px", textAlign: i === 1 ? "left" : "center", border: "1px solid #fff", fontSize: 7 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri} style={{ background: ri % 2 === 0 ? ROW_ODD : ROW_EVEN }}>
              {r.map((cell, ci) => (
                <td key={ci} style={{ padding: "2px 3px", textAlign: ci === 1 ? "left" : "center", fontSize: 7 }}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CommentsTable({ prompt, comments }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 7 }}>
      <thead>
        <tr>
          <th style={{ background: TH_BG, color: TH_COLOR, padding: "3px 5px", textAlign: "left", fontWeight: 600 }}>{prompt}</th>
        </tr>
      </thead>
      <tbody>
        {comments.map((c, i) => (
          <tr key={i} style={{ background: i % 2 === 0 ? ROW_ODD : ROW_EVEN }}>
            <td style={{ padding: "3px 5px", fontSize: 7 }}>{c}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function VariationCards({ items }) {
  return (
    <div>
      <p style={{ fontSize: 7, color: "#333", marginBottom: 6 }}>
        The 5 statements below represent the areas with the greatest differences in respondent scores. The numbers show the distribution of responses across participants.
      </p>
      {items.map(({ id, question, component, std, dist }) => (
        <div key={id} style={{ marginBottom: 6, border: "1px solid #ddd" }}>
          <div style={{ padding: "2px 4px", fontWeight: 600, fontSize: 7 }}>{id}. {question}</div>
          <div style={{ display: "flex", borderTop: "1px solid #ddd" }}>
            {Object.entries(dist).map(([k, v]) => (
              <div key={k} style={{ flex: 1, borderLeft: "1px solid #D9D9D9", padding: "2px 3px", textAlign: "center" }}>
                <div style={{ fontSize: 6, color: "#666" }}>{k}</div>
                <div style={{ fontSize: 8, fontWeight: 700 }}>{v}</div>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", background: "#ECECEC", padding: "2px 4px", fontSize: 7 }}>
            <span>Component: <strong>{component}</strong></span>
            <span>Std Dev: {std}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fixed framework pages — always present at the start of every report
// These match the real Introduction / 5x Model / Scoring Explained pages
// ---------------------------------------------------------------------------

const FIXED_PAGES = [
  {
    title: "Introduction",
    content: (
      <div style={{ fontSize: 8, lineHeight: 1.5, color: "#333" }}>
        <p style={{ marginBottom: 6 }}>This report is designed to provide your leadership team with key insights to align on what is most critical to effectively execute across the five components of The 5x Model. The assessment evaluates two important dimensions:</p>
        <div style={{ marginLeft: 10, marginBottom: 6 }}>
          <p style={{ marginBottom: 2 }}>1. The <strong>importance</strong> of each statement to your organization's success.</p>
          <p>2. The level of <strong>agreement</strong> regarding the execution associated with each statement.</p>
        </div>
        <p>By addressing both dimensions, this assessment identifies where alignment exists and where further discussion or action is needed to achieve optimal results.</p>
      </div>
    ),
  },
  {
    title: "The 5x Model",
    content: (
      <div style={{ fontSize: 8, color: "#333" }}>
        {[
          ["Strategic Clarity", "#004f73", "A clear, well-defined strategy that the entire organization is aligned on and executing against."],
          ["Scalable Talent", "#1a7fa0", "The right people in the right roles with a strong succession plan and bench strength."],
          ["Relentless Focus", "#2a6f90", "Disciplined prioritization — saying no to what does not drive the most value."],
          ["Disciplined Execution", "#7b3fa0", "Consistent follow-through on milestones, KPIs, and accountability across the organization."],
          ["Energized Culture", "#1a6b3c", "An environment of shared purpose, trust, and high engagement that drives performance."],
        ].map(([pillar, color, desc]) => (
          <div key={pillar} style={{ display: "flex", gap: 6, marginBottom: 6, borderLeft: `3px solid ${color}`, paddingLeft: 6 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 8, color }}>{pillar}</div>
              <div style={{ fontSize: 7, color: "#555" }}>{desc}</div>
            </div>
          </div>
        ))}
      </div>
    ),
  },
  {
    title: "The 5x Model: Essential Components",
    content: (
      <div style={{ fontSize: 8, color: "#333" }}>
        <p style={{ marginBottom: 6, fontStyle: "italic" }}>Each pillar contains a set of statements that represent the critical practices for that component. Respondents rate each statement on two dimensions.</p>
        {[
          ["Strategic Clarity", "Value creation plan, board alignment, strategic priorities, market positioning, resource allocation."],
          ["Scalable Talent", "A-player hiring, succession planning, performance management, talent development, org structure."],
          ["Relentless Focus", "Priority discipline, focus metrics, saying no, top 3–5 initiatives, leadership time allocation."],
          ["Disciplined Execution", "Operating cadence, accountability culture, KPI tracking, milestone reviews, consequences."],
          ["Energized Culture", "Engagement, trust, recognition, purpose clarity, collaboration, psychological safety."],
        ].map(([pillar, comps]) => (
          <div key={pillar} style={{ marginBottom: 5, padding: "3px 5px", background: "#f5f5f5", borderRadius: 2 }}>
            <div style={{ fontWeight: 700, fontSize: 7, color: TH_BG, marginBottom: 2 }}>{pillar}</div>
            <div style={{ fontSize: 7, color: "#555" }}>{comps}</div>
          </div>
        ))}
      </div>
    ),
  },
  {
    title: "Scoring Explained: The 5x Team Assessment",
    content: (
      <div style={{ fontSize: 8 }}>
        <div style={{ background: "#eee", padding: 6, marginBottom: 8, borderRadius: 2 }}>
          <p style={{ fontWeight: 700, marginBottom: 4 }}>For each statement, respondents provide ratings on two factors:</p>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <strong style={{ fontSize: 7 }}>1. Importance</strong>
              <div style={{ fontSize: 7, marginTop: 2, lineHeight: 1.6 }}>
                0 = Not Important<br />1 = Important<br />3 = Very Important<br />9 = Critical
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <strong style={{ fontSize: 7 }}>2. Agreement</strong>
              <div style={{ fontSize: 7, marginTop: 2, lineHeight: 1.6 }}>
                1 = Not at All<br />2 = To a Small Extent<br />3 = To a Moderate Extent<br />4 = To a Great Extent<br />5 = Completely
              </div>
            </div>
          </div>
        </div>
        <div style={{ border: "1px solid #ccc", padding: 6, borderRadius: 2, textAlign: "center", fontSize: 8 }}>
          <div style={{ marginBottom: 2 }}>Performance % =</div>
          <div style={{ display: "inline-block", textAlign: "center" }}>
            <div>Avg. Importance × Avg. Agreement</div>
            <div style={{ borderTop: "1px solid #666", marginTop: 2, paddingTop: 2 }}>Avg. Importance × 5</div>
          </div>
          <div style={{ marginTop: 2 }}>× 100</div>
        </div>
      </div>
    ),
  },
];

// ---------------------------------------------------------------------------
// Block pages — each entry maps to one or more real PDF pages
// Structure mirrors the cover-page page-break divs in advancereport.html
// ---------------------------------------------------------------------------

const PILLAR_RANKING_ROWS = {
  "Strategic Clarity": [
    ["1.01", "We have a clear and well-defined value creation plan", "9.00", "4.50", "0.50", "90%"],
    ["1.02", "Our strategy is effectively communicated at all levels", "7.00", "3.80", "1.20", "76%"],
    ["1.03", "Leadership is fully aligned on the top 3–5 strategic priorities", "9.00", "3.60", "1.40", "72%"],
    ["1.04", "Our board / investors are aligned with our strategic direction", "7.00", "3.40", "1.60", "68%"],
  ],
  "Scalable Talent": [
    ["2.01", "Critical roles across the org are filled with A-players", "9.00", "3.80", "1.20", "76%"],
    ["2.02", "We have a strong succession plan for all key roles", "7.00", "3.20", "1.80", "64%"],
    ["2.03", "We consistently raise the bar on talent — removing low performers", "9.00", "3.40", "1.60", "68%"],
    ["2.04", "Our HR/people processes scale effectively with growth", "3.00", "3.60", "1.40", "72%"],
  ],
  "Relentless Focus": [
    ["3.01", "We consistently say no to opportunities outside our core priorities", "7.00", "3.20", "1.80", "64%"],
    ["3.02", "Leadership time is aligned to the highest-value activities", "9.00", "3.60", "1.40", "72%"],
    ["3.03", "We have 3–5 clear company priorities for this year", "7.00", "4.00", "1.00", "80%"],
    ["3.04", "We measure and manage focus rigorously across teams", "3.00", "3.00", "2.00", "60%"],
  ],
  "Disciplined Execution": [
    ["4.01", "We have a structured operating cadence with clear accountability", "9.00", "3.40", "1.60", "68%"],
    ["4.02", "We consistently execute against our milestones and KPIs", "9.00", "3.60", "1.40", "72%"],
    ["4.03", "Underperformance is addressed promptly and constructively", "7.00", "3.00", "2.00", "60%"],
    ["4.04", "We use data to drive decision-making across the organization", "7.00", "3.80", "1.20", "76%"],
  ],
  "Energized Culture": [
    ["5.01", "Employee engagement is high across the organization", "9.00", "3.60", "1.40", "72%"],
    ["5.02", "We have a clear and compelling purpose that drives behavior", "7.00", "3.80", "1.20", "76%"],
    ["5.03", "Trust between leadership and employees is strong", "9.00", "3.40", "1.60", "68%"],
    ["5.04", "Recognition and feedback are embedded in our culture", "3.00", "3.20", "1.80", "64%"],
  ],
};

const PILLAR_COMMENTS = {
  "Strategic Clarity": [
    "We need to cascade the strategy more effectively to middle management — there's a gap between what the leadership team knows and what front-line managers are executing against.",
    "Invest in a quarterly strategy review process that includes all direct reports, not just the C-suite.",
    "The board alignment sessions have improved but still need more structured agenda and pre-reading materials.",
  ],
  "Scalable Talent": [
    "Focus on building our talent pipeline — we're too reliant on a few key individuals and have no clear succession for them.",
    "Implement a structured 90-day onboarding program to accelerate the ramp-up of A-players.",
  ],
  "Relentless Focus": [
    "We say yes too often. Leadership needs to model the behavior of saying no — even to good ideas that distract from our priorities.",
    "Define and communicate the top 3 company priorities at every all-hands and team meeting.",
  ],
  "Disciplined Execution": [
    "Introduce a weekly leadership team operating review focused purely on KPI tracking and blocker resolution.",
    "Create more public accountability for missed milestones — currently there are no consequences.",
    "We need a better project management system that everyone actually uses.",
  ],
  "Energized Culture": [
    "Conduct quarterly pulse surveys and actually act on the results publicly — employees need to see their feedback matters.",
    "Leadership should invest in team off-sites and structured cross-functional collaboration sessions.",
  ],
};

const BLOCK_PREVIEWS = {
  executive_summary: {
    color: "#004f73",
    pages: [
      {
        title: "Summary: 5x Team Assessment",
        content: (
          <div style={{ fontSize: 8 }}>
            <p style={{ marginBottom: 6, color: "#333" }}>
              The 5x performance score measures how effectively the leadership team has executed each component of the 5x model. Your assessment scores indicate that strategic clarity and energized culture execution are high, relentless focus execution is moderate, and scalable talent and disciplined execution have the greatest potential for improvement.
            </p>
            {[
              { label: "Strategic Clarity", pct: 82, color: "#008000" },
              { label: "Scalable Talent", pct: 67, color: "#e6e600" },
              { label: "Relentless Focus", pct: 74, color: "#e6e600" },
              { label: "Disciplined Exec.", pct: 55, color: "#fe0000" },
              { label: "Energized Culture", pct: 79, color: "#008000" },
              { label: "Financial Perf.", pct: 61, color: "#e6e600" },
            ].map(({ label, pct, color }) => (
              <div key={label} style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 3 }}>
                <span style={{ width: 76, fontSize: 7, color: "#555", flexShrink: 0 }}>{label}</span>
                <div style={{ flex: 1, background: "#eee", height: 10, border: "1px solid #bbb", overflow: "hidden" }}>
                  <div style={{ width: `${pct}%`, height: "100%", background: color, borderRight: "1px solid #888" }} />
                </div>
                <span style={{ width: 24, fontSize: 7, color: "#333", textAlign: "right" }}>{pct}%</span>
              </div>
            ))}
            <div style={{ display: "flex", gap: 8, fontSize: 7, color: "#555", marginTop: 4, borderTop: "1px solid #eee", paddingTop: 4 }}>
              <span style={{ color: "#fe0000" }}>● Low (0–59%)</span>
              <span style={{ color: "#b8b800" }}>● Medium (60–79%)</span>
              <span style={{ color: "#008000" }}>● High (80–100%)</span>
            </div>
            <div style={{ marginTop: 8, borderTop: "1px solid #eee", paddingTop: 6 }}>
              {[
                ["STRATEGIC CLARITY", "#004f73", "Your score was high. The organization's strategy is clearly articulated and widely understood by the leadership team."],
                ["SCALABLE TALENT", "#1a7fa0", "Your score was medium. Talent planning gaps remain regarding quality, bench strength, and succession planning."],
                ["RELENTLESS FOCUS", "#2a6f90", "Your score was medium. The team recognizes areas where prioritization discipline can be strengthened."],
              ].map(([pillar, color, desc]) => (
                <div key={pillar} style={{ marginBottom: 5, borderLeft: `2px solid ${color}`, paddingLeft: 5 }}>
                  <div style={{ fontWeight: 700, fontSize: 7, color }}>{pillar}</div>
                  <div style={{ fontSize: 7, color: "#555" }}>{desc}</div>
                </div>
              ))}
            </div>
          </div>
        ),
      },
    ],
  },

  pillar_scores: {
    color: "#1a7fa0",
    pages: [
      {
        title: "Summary: 5x Team Assessment",
        content: (
          <div style={{ fontSize: 8 }}>
            <p style={{ marginBottom: 6, color: "#333" }}>
              The 5x performance score represents the team's aggregate score as a percentage of the maximum potential opportunity. It indicates how effectively each pillar is being executed relative to its stated importance.
            </p>
            {[
              { label: "Strategic Clarity", pct: 82, color: "#008000" },
              { label: "Scalable Talent", pct: 67, color: "#e6e600" },
              { label: "Relentless Focus", pct: 74, color: "#e6e600" },
              { label: "Disciplined Exec.", pct: 55, color: "#fe0000" },
              { label: "Energized Culture", pct: 79, color: "#008000" },
              { label: "Financial Perf.", pct: 61, color: "#e6e600" },
            ].map(({ label, pct, color }) => (
              <div key={label} style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 3 }}>
                <span style={{ width: 76, fontSize: 7, color: "#555", flexShrink: 0 }}>{label}</span>
                <div style={{ flex: 1, background: "#eee", height: 10, border: "1px solid #bbb", overflow: "hidden" }}>
                  <div style={{ width: `${pct}%`, height: "100%", background: color }} />
                </div>
                <span style={{ width: 24, fontSize: 7, color: "#333", textAlign: "right" }}>{pct}%</span>
              </div>
            ))}
            <div style={{ display: "flex", gap: 8, fontSize: 7, color: "#555", marginTop: 4 }}>
              <span style={{ color: "#fe0000" }}>● Low (0–59%)</span>
              <span style={{ color: "#b8b800" }}>● Medium (60–79%)</span>
              <span style={{ color: "#008000" }}>● High (80–100%)</span>
            </div>
          </div>
        ),
      },
    ],
  },

  top_priorities: {
    color: "#2a6f90",
    pages: [
      {
        title: "Highest Importance Areas",
        content: (
          <div>
            <p style={{ fontSize: 7, color: "#333", marginBottom: 4 }}>The statements listed below represent the 5 highest importance areas. These are what assessment respondents consider most critical to driving value creation and organizational success.</p>
            <p style={{ fontSize: 7, color: "#555", marginBottom: 4 }}>Importance Scoring: 0=Not Important, 1=Important, 3=Very Important, 9=Critical</p>
            <ReportTable
              headers={["No.", "Statement", "Avg Importance Score"]}
              rows={[
                ["1.01", "We have a clear and well-defined value creation plan", "9.00"],
                ["2.01", "Critical roles across the org are filled with A-players", "9.00"],
                ["1.03", "Leadership is fully aligned on the top 3–5 strategic priorities", "9.00"],
                ["4.02", "We consistently execute against our milestones and KPIs", "9.00"],
                ["3.02", "Leadership time is aligned to the highest-value activities", "9.00"],
              ]}
            />
          </div>
        ),
      },
      {
        title: "Highest Agreement Areas",
        content: (
          <div>
            <p style={{ fontSize: 7, color: "#333", marginBottom: 4 }}>The statements listed below represent the 8 highest agreement areas. These are the areas of strongest execution based on assessment responses.</p>
            <p style={{ fontSize: 7, color: "#555", marginBottom: 4 }}>Agreement Scoring: 1=Not at All, 2=To a Small Extent, 3=To a Moderate Extent, 4=To a Great Extent, 5=Completely</p>
            <ReportTable
              headers={["No.", "Statement", "Avg Agreement Score"]}
              rows={[
                ["1.01", "We have a clear and well-defined value creation plan", "4.50"],
                ["5.02", "We have a clear and compelling purpose that drives behavior", "3.80"],
                ["3.03", "We have 3–5 clear company priorities for this year", "4.00"],
                ["1.02", "Our strategy is effectively communicated at all levels", "3.80"],
                ["4.04", "We use data to drive decision-making across the organization", "3.80"],
              ]}
            />
          </div>
        ),
      },
      {
        title: "Identifying Overlap: Highest Importance & Highest Agreement",
        content: (
          <div>
            <p style={{ fontSize: 7, color: "#333", marginBottom: 4 }}>The statements listed below highlight any overlap between highest-importance and strongest agreement statements. This comparison helps identify whether the organization's most critical areas are also its most well-executed.</p>
            <ReportTable
              headers={["No.", "Statement", "Avg Importance", "Avg Agreement"]}
              rows={[
                ["1.01", "We have a clear and well-defined value creation plan", "9.00", "4.50"],
                ["3.03", "We have 3–5 clear company priorities for this year", "7.00", "4.00"],
              ]}
            />
          </div>
        ),
      },
    ],
  },

  opportunity_gaps: {
    color: "#c0392b",
    pages: [
      {
        title: "Top Areas for Improvement",
        content: (
          <div>
            <p style={{ fontSize: 7, color: "#333", marginBottom: 4 }}>The statements listed below represent the 5 highest improvement opportunity areas — where importance is high but agreement is low.</p>
            <ReportTable
              headers={["No.", "Statement", "Avg Importance", "Avg Improvement Opportunity"]}
              rows={[
                ["4.03", "Underperformance is addressed promptly and constructively", "7.00", "2.00"],
                ["3.01", "We consistently say no to opportunities outside our core priorities", "7.00", "1.80"],
                ["2.02", "We have a strong succession plan for all key roles", "7.00", "1.80"],
                ["5.01", "Employee engagement is high across the organization", "9.00", "1.40"],
                ["2.03", "We consistently raise the bar on talent", "9.00", "1.60"],
              ]}
            />
          </div>
        ),
      },
      {
        title: "Lowest Agreement Areas",
        content: (
          <div>
            <p style={{ fontSize: 7, color: "#333", marginBottom: 4 }}>The statements listed below represent the 5 lowest agreement areas — the areas where execution is currently weakest relative to other statements.</p>
            <ReportTable
              headers={["No.", "Statement", "Avg Agreement Score"]}
              rows={[
                ["3.04", "We measure and manage focus rigorously across teams", "3.00"],
                ["4.03", "Underperformance is addressed promptly and constructively", "3.00"],
                ["2.02", "We have a strong succession plan for all key roles", "3.20"],
                ["5.04", "Recognition and feedback are embedded in our culture", "3.20"],
                ["3.01", "We consistently say no to opportunities outside our core priorities", "3.20"],
              ]}
            />
          </div>
        ),
      },
    ],
  },

  actionable_recommendations: {
    color: "#004f73",
    pages: [
      {
        title: "Actionable Recommendations",
        content: (
          <div style={{ fontSize: 7, color: "#333", lineHeight: 1.4 }}>
            <p style={{ marginBottom: 6 }}>
              Three-column synthesis: protect / improve · most important actions by 5x component · signal quotes and key risk.
            </p>
            <p style={{ margin: 0, fontWeight: 600, color: "#004f73" }}>Most Important Actions by Component</p>
            <ul style={{ margin: "4px 0 0", paddingLeft: 14 }}>
              <li>Strategic Clarity — translate priorities into measurable outcomes</li>
              <li>Scalable Talent — close bench and compensation gaps</li>
              <li>Disciplined Execution — strengthen MOS and follow-through</li>
            </ul>
          </div>
        ),
      },
    ],
  },

  variance_analysis: {
    color: "#7b3fa0",
    pages: [
      {
        title: "Key Areas of Difference: Level of Importance Variation",
        content: (
          <VariationCards items={[
            { id: "1.03", question: "Leadership is fully aligned on the top 3–5 strategic priorities", component: "Strategic Clarity", std: "1.83", dist: { "Not Imp.": 0, "Important": 1, "Very Imp.": 2, "Critical": 3 } },
            { id: "4.04", question: "We use data to drive decision-making across the organization", component: "Disciplined Execution", std: "1.52", dist: { "Not Imp.": 0, "Important": 2, "Very Imp.": 3, "Critical": 1 } },
            { id: "2.03", question: "We consistently raise the bar on talent — removing low performers", component: "Scalable Talent", std: "1.45", dist: { "Not Imp.": 1, "Important": 0, "Very Imp.": 2, "Critical": 3 } },
            { id: "5.04", question: "Recognition and feedback are embedded in our culture", component: "Energized Culture", std: "1.38", dist: { "Not Imp.": 0, "Important": 2, "Very Imp.": 4, "Critical": 0 } },
            { id: "3.01", question: "We consistently say no to opportunities outside our core priorities", component: "Relentless Focus", std: "1.22", dist: { "Not Imp.": 0, "Important": 1, "Very Imp.": 3, "Critical": 2 } },
          ]} />
        ),
      },
      {
        title: "Key Areas of Difference: Level of Agreement Variation",
        content: (
          <VariationCards items={[
            { id: "4.01", question: "We have a structured operating cadence with clear accountability", component: "Disciplined Execution", std: "1.76", dist: { "Not at All": 0, "Small Ext.": 1, "Mod. Ext.": 3, "Great Ext.": 2, "Completely": 0 } },
            { id: "2.02", question: "We have a strong succession plan for all key roles", component: "Scalable Talent", std: "1.58", dist: { "Not at All": 1, "Small Ext.": 2, "Mod. Ext.": 2, "Great Ext.": 1, "Completely": 0 } },
            { id: "5.03", question: "Trust between leadership and employees is strong", component: "Energized Culture", std: "1.41", dist: { "Not at All": 0, "Small Ext.": 1, "Mod. Ext.": 2, "Great Ext.": 3, "Completely": 0 } },
            { id: "3.04", question: "We measure and manage focus rigorously across teams", component: "Relentless Focus", std: "1.22", dist: { "Not at All": 1, "Small Ext.": 2, "Mod. Ext.": 2, "Great Ext.": 1, "Completely": 0 } },
          ]} />
        ),
      },
    ],
  },

  financial_performance: {
    color: "#1a6b3c",
    pages: [
      {
        title: "Financial Performance",
        content: (
          <div>
            <ReportTable
              headers={["No.", "Statement", "Avg Agreement Score"]}
              rows={[
                ["6.01", "We are on track to achieve our value creation targets", "3.80"],
                ["6.02", "Revenue growth is meeting or exceeding plan", "3.40"],
                ["6.03", "EBITDA / margin improvement is on track", "3.20"],
                ["6.04", "We are on track to achieve our targeted MOIC", "3.20"],
              ]}
            />
            <div style={{ marginTop: 8 }}>
              <ReportTable
                headers={["No.", "Statement", "Avg Hold Period Response"]}
                rows={[["6.05", "What is your current expected hold period (in years)?", "4.20 years"]]}
              />
            </div>
            <div style={{ marginTop: 8 }}>
              <ReportTable
                headers={["No.", "Statement", "Timing", "# of responses"]}
                rows={[
                  ["6.06", "How is your exit timeline tracking vs. expectations?", "Earlier than expected", "2"],
                  ["", "", "On track with expectations", "5"],
                  ["", "", "Later than expected", "1"],
                ]}
              />
            </div>
          </div>
        ),
      },
      {
        title: "Team Comments on Financial Performance",
        content: (
          <CommentsTable
            prompt="In order to achieve greater financial performance, what is the most important action the leadership team needs to take and why?"
            comments={[
              "We need to improve our unit economics before accelerating growth — scaling unprofitable revenue is not the answer.",
              "Focus on reducing churn and improving net revenue retention — that's the fastest path to MOIC improvement.",
              "The leadership team should align on a clear capital allocation framework to prioritize the highest-return investments.",
            ]}
          />
        ),
      },
    ],
  },

  individual_breakdown: {
    color: "#7a5c00",
    pages: Object.entries(PILLAR_RANKING_ROWS).flatMap(([pillar, rows]) => [
      {
        title: `${pillar} Ranking`,
        content: <PillarRankingTable pillar={pillar} rows={rows} />,
      },
      {
        title: `Team Comments on ${pillar}`,
        content: (
          <CommentsTable
            prompt={`In order to achieve greater success in realizing ${pillar.toLowerCase()}, what is the most important action the leadership team needs to take and why?`}
            comments={PILLAR_COMMENTS[pillar] || []}
          />
        ),
      },
    ]),
  },

  team_pillar_rankings: {
    color: "#5b21b6",
    pages: Object.entries(PILLAR_RANKING_ROWS).flatMap(([pillar, rows]) => [
      {
        title: `${pillar} Ranking`,
        content: <PillarRankingTable pillar={pillar} rows={rows} />,
      },
      {
        title: `Team Comments on ${pillar}`,
        content: (
          <CommentsTable
            prompt={`In order to achieve greater success in realizing ${pillar.toLowerCase()}, what is the most important action the leadership team needs to take and why?`}
            comments={PILLAR_COMMENTS[pillar] || []}
          />
        ),
      },
    ]),
  },
};

// ---------------------------------------------------------------------------
// Single PDF page card — mirrors one cover-page page-break div from the template
// ---------------------------------------------------------------------------

function PdfPageCard({ title, content, pageNum, borderColor }) {
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 4,
        boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
        marginBottom: 10,
        overflow: "hidden",
        border: `1px solid #e5e7eb`,
      }}
    >
      {/* Page header — matches h1 style from the real template */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "5px 8px", borderBottom: `2px solid ${borderColor || "#004f73"}`, background: "#fafafa" }}>
        <span style={{ fontSize: 9, fontWeight: 700, color: "#2c3e50", letterSpacing: 0 }}>{title}</span>
        <span style={{ fontSize: 7, color: "#aaa", flexShrink: 0 }}>{pageNum}</span>
      </div>
      {/* Page content */}
      <div style={{ padding: "6px 8px" }}>{content}</div>
      {/* Footer — matches the report footer */}
      <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 8px", background: "#f9f9f9", borderTop: "1px solid #eee" }}>
        <span style={{ fontSize: 6, color: "#ccc" }}>5x Assessment Platform</span>
        <span style={{ fontSize: 6, color: "#ccc" }}>Confidential</span>
      </div>
    </div>
  );
}

function PdfPreview({
  blocks,
  catalog: _catalog,
  profileName,
  targetRole,
  profileId = "",
  companies = [],
  loadingCompanies = false,
}) {
  const enabledBlocks = normalizeDraftBlocks(blocks)
    .filter((b) => b.enabled !== false)
    .sort((a, b) => a.order - b.order);
  const [companyName, setCompanyName] = useState("");
  const [generating, setGenerating] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    if (!fullscreen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setFullscreen(false);
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [fullscreen]);

  // Build the full ordered list of pages — fixed pages + per-block pages
  const allPages = [];
  FIXED_PAGES.forEach((p) => allPages.push({ ...p, blockId: null, color: "#555" }));
  enabledBlocks.forEach((b) => {
    const type = b.block_type || b.block_id;
    const def = BLOCK_PREVIEWS[type];
    if (!def) return;
    (def.pages || []).forEach((p) => allPages.push({ ...p, blockId: type, color: def.color }));
  });

  // Build TOC entries
  const tocEntries = [];
  FIXED_PAGES.forEach((p, i) => tocEntries.push({ title: p.title, page: i + 2 }));
  let pg = FIXED_PAGES.length + 2;
  enabledBlocks.forEach((b) => {
    const type = b.block_type || b.block_id;
    const def = BLOCK_PREVIEWS[type];
    if (!def) return;
    (def.pages || []).forEach((p) => {
      tocEntries.push({ title: p.title, page: pg, color: def.color });
      pg++;
    });
  });
  const totalPages = pg;

  const runGeneratePdf = async () => {
    if (!profileId) {
      toast.error("Save the profile first to generate a PDF");
      return;
    }
    if (!companyName.trim()) {
      toast.error("Select a company");
      return;
    }
    setGenerating(true);
    toast.promise(
      openModularReportPdf(companyName.trim(), profileId, targetRole || "").finally(() =>
        setGenerating(false)
      ),
      {
        loading: "Generating PDF…",
        success: "PDF opened",
        error: (e) => e?.message || "Could not generate PDF",
      }
    );
  };

  const previewPages = (
    <>
      {/* ── Page 1: Cover ── */}
      <div style={{ background: TH_BG, borderRadius: 4, boxShadow: "0 1px 4px rgba(0,0,0,0.15)", marginBottom: 10, padding: "16px 12px", color: "#fff" }}>
        <div style={{ fontSize: 7, fontWeight: 600, letterSpacing: 2, color: "#90c4dc", textTransform: "uppercase", marginBottom: 4 }}>Confidential — The 5x Assessment</div>
        <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.3, marginBottom: 4 }}>{profileName || "Report Profile"}</div>
        {targetRole && <div style={{ fontSize: 8, color: "#a8d4e8", marginBottom: 2 }}>Prepared for: {targetRole}</div>}
        <div style={{ fontSize: 7, color: "#7ab5cc", marginTop: 8 }}>{new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}</div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, paddingTop: 6, borderTop: "1px solid rgba(255,255,255,0.15)", fontSize: 6, color: "rgba(255,255,255,0.4)" }}>
          <span>5x Assessment Platform</span><span>Page 1</span>
        </div>
      </div>

      {/* ── Page 2: Table of Contents ── */}
      {enabledBlocks.length > 0 && (
        <div style={{ background: "#fff", borderRadius: 4, boxShadow: "0 1px 4px rgba(0,0,0,0.12)", marginBottom: 10, overflow: "hidden" }}>
          <div style={{ padding: "5px 8px", borderBottom: `2px solid ${TH_BG}`, background: "#fafafa", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 9, fontWeight: 700, color: "#2c3e50" }}>Table of Contents</span>
            <span style={{ fontSize: 7, color: "#aaa" }}>2</span>
          </div>
          <div style={{ padding: "6px 8px" }}>
            {tocEntries.map((entry, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-end", gap: 4, marginBottom: 2 }}>
                <span style={{ fontSize: 7, color: entry.color || "#555", flexShrink: 0, minWidth: 120, lineHeight: 1.3 }}>{entry.title}</span>
                <div style={{ flex: 1, borderBottom: "1px dotted #bbb", marginBottom: 2 }} />
                <span style={{ fontSize: 7, color: "#888", flexShrink: 0 }}>{entry.page}</span>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 8px", background: "#f9f9f9", borderTop: "1px solid #eee" }}>
            <span style={{ fontSize: 6, color: "#ccc" }}>5x Assessment Platform</span>
            <span style={{ fontSize: 6, color: "#ccc" }}>Confidential</span>
          </div>
        </div>
      )}

      {/* ── Pages 3+: Fixed + block pages ── */}
      {enabledBlocks.length === 0 ? (
        <div style={{ textAlign: "center", color: "#bbb", fontSize: 11, padding: 32 }}>
          Add blocks on the left to see your report take shape
        </div>
      ) : (
        allPages.map((page, i) => (
          <PdfPageCard
            key={`${page.blockId || "fixed"}-${i}`}
            title={page.title}
            content={page.content}
            pageNum={i + 3}
            borderColor={page.color}
          />
        ))
      )}
    </>
  );

  const toolbar = (
    <div className="px-4 py-2 border-b border-gray-300 bg-white shrink-0 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            PDF Preview
          </span>
          <span className="text-[10px] text-gray-400 truncate">
            {totalPages} pages · {enabledBlocks.length} block
            {enabledBlocks.length !== 1 ? "s" : ""}
          </span>
        </div>
        <button
          type="button"
          onClick={() => setFullscreen((open) => !open)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-50 hover:border-[#004f73]/35 hover:text-[#004f73] transition-colors shrink-0"
          title={fullscreen ? "Exit full screen (Esc)" : "Full screen preview"}
          aria-pressed={fullscreen}
        >
          {fullscreen ? (
            <>
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5" aria-hidden>
                <path d="M6 4a1 1 0 00-1 1v2a1 1 0 11-2 0V5a3 3 0 013-3h2a1 1 0 110 2H6zm8 0a1 1 0 011 1v2a1 1 0 102 0V5a3 3 0 00-3-3h-2a1 1 0 100 2h2zM5 14a1 1 0 011 1v0a1 1 0 001 1h2a1 1 0 110 2H7a3 3 0 01-3-3v-1a1 1 0 011-1zm10 0a1 1 0 011 1v1a3 3 0 01-3 3h-2a1 1 0 110-2h2a1 1 0 001-1v0a1 1 0 011-1z" />
              </svg>
              Exit full screen
            </>
          ) : (
            <>
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5" aria-hidden>
                <path d="M3 4a1 1 0 011-1h3a1 1 0 010 2H5v2a1 1 0 01-2 0V4zm12-1a1 1 0 011 1v3a1 1 0 11-2 0V5h-2a1 1 0 110-2h3zM4 12a1 1 0 011 1v2h2a1 1 0 110 2H4a1 1 0 01-1-1v-3a1 1 0 011-1zm12 0a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 110-2h2v-2a1 1 0 011-1z" />
              </svg>
              Full screen
            </>
          )}
        </button>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex-1 min-w-[140px] text-xs">
          <span className="font-semibold text-slate-600 mb-1 block">Company</span>
          <select
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            disabled={loadingCompanies}
            className="w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm bg-white"
          >
            <option value="">
              {loadingCompanies ? "Loading companies…" : "Select company…"}
            </option>
            {(companies || []).map((c) => {
              const cName = typeof c === "string" ? c : c.company_name || c.name || "";
              if (!cName) return null;
              return (
                <option key={cName} value={cName}>
                  {cName}
                </option>
              );
            })}
          </select>
        </label>
        <button
          type="button"
          onClick={runGeneratePdf}
          disabled={generating || !profileId}
          className="rounded-lg bg-[#004f73] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#003a57] disabled:opacity-50"
          title={!profileId ? "Save the profile first" : "Generate PDF"}
        >
          {generating ? "Generating…" : "Generate PDF"}
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div className="h-full min-h-0 flex flex-col bg-gray-200 overflow-hidden">
        {toolbar}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4">
          {previewPages}
        </div>
      </div>

      {fullscreen
        ? createPortal(
            <div
              className="report-pdf-preview-fullscreen fixed inset-0 z-[110] flex flex-col bg-slate-900/40"
              role="dialog"
              aria-modal="true"
              aria-label="PDF preview full screen"
            >
              <div className="flex-1 min-h-0 m-2 sm:m-4 flex flex-col bg-gray-200 rounded-xl shadow-2xl overflow-hidden border border-slate-200">
                {toolbar}
                <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 sm:p-6">
                  <div className="mx-auto w-full max-w-3xl report-pdf-preview-fullscreen-pages">
                    {previewPages}
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
// Profile editor modal — full-screen split layout
// ---------------------------------------------------------------------------

export function ProfileEditorModal({
  profile,
  catalog,
  onSave,
  onClose,
  reportBase = "company",
  embedded = false,
}) {
  const isNew = !profile?._id;
  const isTeamBase = isTeamReportBase(reportBase) || profile?.is_team_base;
  const [assessmentFormats, setAssessmentFormats] = useState([]);
  const [assessmentTypeId, setAssessmentTypeId] = useState(profile?.assessment_type_id || "");
  const selectedFormat = useMemo(() => {
    if (!assessmentTypeId) {
      return (assessmentFormats || []).find((f) => isFiveXAssessmentFormat(f)) || null;
    }
    return (assessmentFormats || []).find((f) => f.id === assessmentTypeId) || null;
  }, [assessmentFormats, assessmentTypeId]);
  const filteredCatalog = filterCatalogForProfile(
    catalog,
    isTeamBase ? "team" : "company",
    selectedFormat
  );
  // For new profiles: pick blocks based on the selected assessment format.
  // Generic (non-5X) formats get section_scores-based defaults; 5X gets classic blocks.
  const defaultBlocks = defaultBlocksForBase(isTeamBase ? "team" : "company", selectedFormat);
  // Role picker: PE titles for 5X, neutral titles for custom formats.
  const roles = getRolesForFormat(selectedFormat);
  const [name, setName] = useState(profile?.name || "");
  const [description, setDescription] = useState(profile?.description || "");
  const [targetRole, setTargetRole] = useState(profile?.target_role || "");
  const [blocks, setBlocks] = useState(() =>
    normalizeDraftBlocks(profile?.blocks?.length ? profile.blocks : defaultBlocks)
  );
  const [saving, setSaving] = useState(false);
  const [mobilePanel, setMobilePanel] = useState("blocks");
  const [mobileAssistOpen, setMobileAssistOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [assignmentPrefs, setAssignmentPrefs] = useState(() => defaultAssignmentPrefs());
  const [savedProfileId, setSavedProfileId] = useState(
    () => profile?.profile_id || profile?._id || ""
  );
  const scopeLabels = assessmentFormatSectionLabels(selectedFormat);
  const formatIsFiveX = isFiveXAssessmentFormat(selectedFormat);
  const prevFormatIsFiveX = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetchAssessmentFormats()
      .then((res) => {
        if (cancelled) return;
        const formats = res?.data?.formats || [];
        setAssessmentFormats(formats);
        if (!profile?.assessment_type_id) {
          const fiveX = formats.find((f) => isFiveXAssessmentFormat(f));
          if (fiveX?.id) setAssessmentTypeId(fiveX.id);
        }
      })
      .catch(() => {
        if (!cancelled) setAssessmentFormats([]);
      });
    return () => {
      cancelled = true;
    };
  }, [profile?.assessment_type_id]);

  // Drop 5X-only blocks when switching away from classic 5X
  useEffect(() => {
    const switchedAway =
      prevFormatIsFiveX.current === true && formatIsFiveX === false;
    prevFormatIsFiveX.current = formatIsFiveX;
    if (formatIsFiveX) return;
    setBlocks((prev) => {
      const next = (prev || []).filter((b) => {
        const type = b.block_type || b.block_id;
        const meta = (catalog || []).find((c) => c.block_id === type);
        return !meta?.five_x_only;
      });
      if (switchedAway && next.length !== (prev || []).length) {
        toast("Removed 5X-only blocks for this assessment format", { icon: "ℹ️" });
      }
      return normalizeDraftBlocks(next);
    });
  }, [formatIsFiveX, catalog]);

  useEffect(() => {
    let cancelled = false;
    setLoadingCompanies(true);
    fetchAssignmentCompanies()
      .then((res) => {
        if (!cancelled) setCompanies(res?.data?.companies || []);
      })
      .catch(() => {
        if (!cancelled) setCompanies([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingCompanies(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const applyAssignment = async (profileId) => {
    const patch = assignmentPrefsToPlanPatch(assignmentPrefs, isTeamBase);
    return assignProfileBulk({
      profile_id: profileId,
      all_companies: patch.assign_to_all_companies,
      org_ids: patch.assign_to_company ? [patch.assign_to_company] : [],
      assign_as: patch.assign_as,
    });
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Profile name is required");
      return;
    }
    const normalizedBlocks = normalizeDraftBlocks(blocks);
    if (!normalizedBlocks.some((b) => b.enabled !== false)) {
      toast.error("At least one block must be enabled");
      return;
    }
    if (
      assignmentPrefs.companyScope === "one" &&
      !(assignmentPrefs.companyName || "").trim()
    ) {
      toast.error("Select a company or choose All companies");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        target_role: targetRole || null,
        output_format: "pdf",
        blocks: normalizedBlocks,
        is_company_base: !isTeamBase,
        is_team_base: isTeamBase,
        assessment_type_id: assessmentTypeId || null,
        assessment_format_id: assessmentTypeId || null,
      };
      let savedProfile = null;
      if (isNew) {
        const res = await createProfile(payload);
        savedProfile = res.data?.profile || null;
        const profileId = savedProfile?.profile_id || savedProfile?._id;
        if (profileId) setSavedProfileId(profileId);
        if (profileId) {
          try {
            const assignRes = await applyAssignment(profileId);
            const count = assignRes?.data?.count || 0;
            const roleNote = targetRole ? ` (${targetRole} invites)` : "";
            toast.success(
              count
                ? `Profile created and assigned to ${count} company${count === 1 ? "" : "ies"}${roleNote}`
                : "Profile created (no matching companies to assign)"
            );
          } catch (assignErr) {
            toast.error(assignErr?.message || "Profile saved but assignment failed");
          }
        } else {
          toast.success("Profile created");
        }
        onSave(savedProfile);
      } else {
        const profileId = profile.profile_id || profile._id;
        setSavedProfileId(profileId);
        await updateProfile(profileId, payload);
        try {
          const assignRes = await applyAssignment(profileId);
          const count = assignRes?.data?.count || 0;
          const roleNote = targetRole ? ` (${targetRole} invites)` : "";
          toast.success(
            count
              ? `Profile updated and assigned to ${count} company${count === 1 ? "" : "ies"}${roleNote}`
              : "Profile updated (no matching companies to assign)"
          );
        } catch (assignErr) {
          toast.error(assignErr?.message || "Profile saved but assignment failed");
        }
        onSave(profile);
      }
    } catch (err) {
      toast.error(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const applyAssistDraft = ({ name: n, description: d, targetRole: r, blocks: b }) => {
    if (n) setName(n);
    if (d != null) setDescription(d);
    if (r != null) setTargetRole(r);
    if (b?.length) setBlocks(normalizeDraftBlocks(b));
  };

  const renderAssistPanel = () => (
    <ProfileBuilderAssist
      name={name}
      description={description}
      targetRole={targetRole}
      isTeamBase={isTeamBase}
      isNew={isNew}
      blocks={blocks}
      catalog={filteredCatalog}
      onAssignmentChange={setAssignmentPrefs}
      assignmentPrefs={assignmentPrefs}
      onApply={applyAssistDraft}
    />
  );

  const editorShell = (
    <div
      className={
        embedded
          ? "report-profile-editor-shell report-profile-editor-shell--inline flex flex-col bg-white w-full min-h-0 overflow-hidden rounded-xl border border-slate-200/80 shadow-sm"
          : "report-profile-editor-shell flex flex-col bg-white w-full h-full max-h-[100dvh]"
      }
    >
        {/* ── Top bar ── */}
        <div className="report-profile-editor-topbar flex items-center justify-between px-4 sm:px-6 py-3 border-b border-slate-200 bg-white shrink-0 gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={onClose}
              className="text-slate-400 hover:text-slate-700 p-1.5 rounded-lg hover:bg-slate-100 transition-colors shrink-0"
              title="Close"
            >
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-slate-900 truncate">
                {isNew ? `New ${isTeamBase ? "Team" : "Company"} Report Profile` : profile.name}
              </h2>
              <p className="text-[11px] text-slate-400 mt-0.5 truncate">
                Manual recipe builder · chat assist optional
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setMobilePanel((panel) => (panel === "blocks" ? "preview" : "blocks"))}
              className="lg:hidden text-xs font-medium text-[#004f73] border border-[#004f73]/25 rounded-lg px-3 py-2 hover:bg-[#004f73]/5 transition-colors"
            >
              {mobilePanel === "blocks" ? "Preview" : "Edit blocks"}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-3 sm:px-5 py-2 text-xs sm:text-sm font-medium text-white bg-[#004f73] hover:bg-[#003a57] rounded-xl shadow-sm disabled:opacity-60 transition-colors shrink-0"
            >
              {saving && <Spinner className="h-4 w-4" />}
              {isNew ? "Create Profile" : "Save Changes"}
            </button>
          </div>
        </div>

        {/* ── Split body ── */}
        <div className="report-profile-editor-body flex flex-1 min-h-0 overflow-hidden flex-col lg:flex-row">

          {/* ── LEFT: Configuration + block builder (one scroll pane) ── */}
          <div
            className={`report-profile-editor-sidebar w-full lg:w-[min(520px,42vw)] shrink-0 flex flex-col border-r border-slate-200 bg-slate-50/30 min-h-0 overflow-hidden ${
              mobilePanel === "preview" ? "hidden lg:flex" : "flex"
            }`}
          >
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
              {/* Profile meta */}
              <div className="report-profile-form-head px-5 py-4 space-y-3 border-b border-slate-100 bg-white">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  Profile details
                </p>
                {isNew && !isTeamBase ? (
                  <div className="space-y-2">
                    <label className="block text-xs font-semibold text-gray-600" htmlFor="editor-preset">
                      Start from preset
                    </label>
                    <select
                      id="editor-preset"
                      defaultValue={COMPANY_PROFILE_PRESETS[0]?.id || ""}
                      onChange={(e) => {
                        const preset = COMPANY_PROFILE_PRESETS.find((p) => p.id === e.target.value);
                        if (!preset) return;
                        setName(preset.label);
                        setDescription(preset.description);
                        setBlocks(normalizeDraftBlocks(preset.blocks));
                      }}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#004f73]/40"
                    >
                      {COMPANY_PROFILE_PRESETS.map((preset) => (
                        <option key={preset.id} value={preset.id}>
                          {preset.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Profile Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#004f73]/40"
                    placeholder="e.g. CEO Full Report"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Target Role</label>
                    <select
                      value={targetRole}
                      onChange={(e) => setTargetRole(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#004f73]/40"
                    >
                      <option value="">All roles</option>
                      {roles.filter(Boolean).map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>
                  <ProfileAssignmentForm
                    idPrefix="editor-assign"
                    variant="builder"
                    isTeamBase={isTeamBase}
                    targetRole={targetRole}
                    companies={companies}
                    loadingCompanies={loadingCompanies}
                    value={assignmentPrefs}
                    onChange={setAssignmentPrefs}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Description</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={2}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#004f73]/40 resize-y min-h-[2.75rem]"
                    placeholder="What this profile is for…"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Assessment format
                  </label>
                  <select
                    value={assessmentTypeId}
                    onChange={(e) => setAssessmentTypeId(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#004f73]/40"
                  >
                    {(assessmentFormats || []).length === 0 ? (
                      <option value="">Loading formats…</option>
                    ) : (
                      assessmentFormats.map((fmt) => (
                        <option key={fmt.id} value={fmt.id}>
                          {fmt.name || fmt.key}
                          {fmt.is_system ? " (system)" : ""}
                        </option>
                      ))
                    )}
                  </select>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-2.5">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">
                    Report scope
                  </p>
                  <p className="text-xs text-slate-700">
                    Pillars/sections in this report:{" "}
                    {scopeLabels.length
                      ? `${scopeLabels.join(", ")} (${scopeLabels.length})`
                      : "Classic 5X (6 sections)"}
                  </p>
                  {!formatIsFiveX ? (
                    <p className="text-[11px] text-slate-500 mt-1">
                      5X-only blocks (framework intro, financial performance, team pillar rankings)
                      are hidden for this format.
                    </p>
                  ) : null}
                </div>
              </div>

              {/* Mobile-only collapsible AI assist */}
              <div className="lg:hidden border-b border-slate-100 bg-white">
                <button
                  type="button"
                  onClick={() => setMobileAssistOpen((open) => !open)}
                  aria-expanded={mobileAssistOpen}
                  className="w-full flex items-center justify-between gap-3 px-5 py-3 text-left hover:bg-slate-50/80 transition-colors"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#004f73]/10 text-[#004f73] shrink-0">
                      <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5" aria-hidden>
                        <path d="M11.3 1.046a1 1 0 011.414 0l1.172 1.172a1 1 0 010 1.414l-.707.707 1.414 1.414.707-.707a1 1 0 011.414 0l1.172 1.172a1 1 0 010 1.414l-1.172 1.172a1 1 0 01-1.414 0l-.707-.707-1.414 1.414.707.707a1 1 0 01-1.414 0l-1.172-1.172a1 1 0 010-1.414l.707-.707-1.414-1.414-.707.707a1 1 0 01-1.414 0L9.13 4.818a1 1 0 010-1.414l1.172-1.172zM7 11a1 1 0 011-1h4a1 1 0 110 2H8a1 1 0 01-1-1z" />
                      </svg>
                    </span>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-[#004f73]">AI profile assist</p>
                      <p className="text-[10px] text-slate-500 truncate">Build, refine, or ask about blocks</p>
                    </div>
                  </div>
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className={`w-4 h-4 text-slate-400 shrink-0 transition-transform duration-200 ${
                      mobileAssistOpen ? "rotate-180" : ""
                    }`}
                    aria-hidden
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                {mobileAssistOpen ? <div className="px-5 pb-4">{renderAssistPanel()}</div> : null}
              </div>

              {/* Block builder */}
              <div className="report-profile-blocks px-5 py-4 bg-white">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-3">
                  Report blocks <span className="text-red-500">*</span>
                </p>
                <ReportBlockBuilder
                  catalog={filteredCatalog}
                  selectedBlocks={blocks}
                  onChange={setBlocks}
                  touchReorder
                  sectionLabels={scopeLabels}
                />
              </div>
            </div>
          </div>

          {/* ── RIGHT: AI assist + PDF preview ── */}
          <div
            className={`report-profile-editor-preview flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden bg-gray-100 ${
              mobilePanel === "blocks" ? "hidden lg:flex" : "flex"
            }`}
          >
            <div className="hidden lg:block shrink-0 max-h-[min(38vh,360px)] overflow-y-auto overscroll-contain px-4 py-3 border-b border-gray-200 bg-white">
              {renderAssistPanel()}
            </div>
            <div className="flex-1 min-h-0 overflow-hidden">
              <PdfPreview
                blocks={blocks}
                catalog={filteredCatalog}
                profileName={name}
                targetRole={targetRole}
                profileId={savedProfileId}
                companies={companies}
                loadingCompanies={loadingCompanies}
              />
            </div>
          </div>
        </div>
    </div>
  );

  // Studio: keep editor inside the Reports panel (sidebar + lenses stay visible).
  // Standalone / Engagement: portal a true viewport modal so it is not clipped by glass panels.
  if (embedded) return editorShell;

  return createPortal(
    <div className="report-profile-editor-modal fixed inset-0 z-[100] flex flex-col bg-black/50">
      {editorShell}
    </div>,
    document.body
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function ReportProfilesPage({ embedded = false }) {
  const { setAdminAssistantContext } = useOutletContext() || {};
  const [profiles, setProfiles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProfile, setEditingProfile] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editorReportBase, setEditorReportBase] = useState("company");
  const [reportBase, setReportBase] = useState("company");
  const [deleting, setDeleting] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    try {
      const saved = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
      return saved === "tiles" ? "tiles" : "list";
    } catch {
      return "list";
    }
  });
  const [blocksExpanded, setBlocksExpanded] = useState(false);

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    try {
      localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
    } catch {
      /* ignore */
    }
  };

  const load = useCallback(async (signal) => {
    setLoading(true);
    try {
      const [profilesRes, catalogRes] = await Promise.all([
        fetchProfiles(true, { signal }),
        fetchBlockCatalog({ signal }),
      ]);
      setProfiles(profilesRes.data?.profiles || []);
      setCatalog(catalogRes.data?.blocks || []);
    } catch (err) {
      if (isAbortError(err, signal)) return;
      toast.error(err.message || "Failed to load profiles");
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const handleNew = (reportBase = "company") => {
    setEditingProfile(null);
    setEditorReportBase(reportBase);
    setShowEditor(true);
  };

  const handleEdit = (profile) => {
    setEditingProfile(profile);
    const base = profile.is_team_base ? "team" : "company";
    setEditorReportBase(base);
    setReportBase(base);
    setShowEditor(true);
  };

  const handleDelete = async (profile) => {
    if (profile.is_system) {
      toast.error("System profiles cannot be deleted.");
      return;
    }
    if (!window.confirm(`Delete profile "${profile.name}"?`)) return;
    setDeleting(profile._id);
    try {
      await deleteProfile(profile.profile_id || profile._id);
      toast.success("Profile deleted");
      load();
    } catch (err) {
      toast.error(err.message || "Delete failed");
    } finally {
      setDeleting(null);
    }
  };

  const handleSaved = () => {
    setShowEditor(false);
    load();
  };

  const companyProfiles = useMemo(
    () =>
      filterProfilesForWorkspace(profiles).filter((p) => p.is_team_base !== true),
    [profiles]
  );
  const teamProfiles = useMemo(
    () =>
      filterProfilesForWorkspace(profiles).filter((p) => p.is_team_base === true),
    [profiles]
  );

  const activeProfiles = reportBase === "team" ? teamProfiles : companyProfiles;

  useEffect(() => {
    if (!setAdminAssistantContext || loading) return;
    setAdminAssistantContext(
      buildReportProfilesContext({
        profiles,
        catalog,
        reportBase,
        showEditor,
        editingProfile,
        embedded,
      })
    );
  }, [
    profiles,
    catalog,
    reportBase,
    showEditor,
    editingProfile,
    loading,
    setAdminAssistantContext,
  ]);

  useEffect(() => {
    // When embedded in Studio, StudioPage owns the assistant-context lifecycle.
    // Clearing here on StrictMode remount aborts sibling state and looks like API failures.
    if (embedded) return undefined;
    return () => setAdminAssistantContext?.(null);
  }, [setAdminAssistantContext, embedded]);

  const visibleCatalog = useMemo(() => {
    if (reportBase === "team") {
      return catalog.filter((b) => !b.company_base_only && b.block_id !== "individual_breakdown");
    }
    return catalog.filter((b) => !b.team_base_only);
  }, [catalog, reportBase]);

  const catalogMap = useMemo(
    () => Object.fromEntries(catalog.map((c) => [c.block_id, c.name])),
    [catalog]
  );

  const profileActions = (profile) => (
    <div className="flex items-center gap-1 shrink-0">
      <button
        type="button"
        onClick={() => handleEdit(profile)}
        className="px-3 py-1.5 text-xs font-medium text-[#004f73] rounded-lg hover:bg-[#004f73]/8 transition-colors"
      >
        Edit
      </button>
      {!profile.is_system && (
        <button
          type="button"
          onClick={() => handleDelete(profile)}
          disabled={deleting === profile._id}
          className="px-3 py-1.5 text-xs font-medium text-slate-500 rounded-lg hover:bg-red-50 hover:text-red-600 disabled:opacity-50 transition-colors"
        >
          {deleting === profile._id ? <Spinner className="h-3 w-3" /> : "Delete"}
        </button>
      )}
    </div>
  );

  const profileMeta = (profile, enabledBlocks) => (
    <>
      <div className="flex items-center gap-1.5 flex-wrap">
        <h3 className="text-sm font-semibold text-slate-900 tracking-tight">{profile.name}</h3>
        {profile.is_default && <DefaultBadge />}
        <RoleBadge role={profile.target_role} />
        {profile.is_system && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-50 text-slate-400 font-medium">
            System
          </span>
        )}
      </div>
      {profile.description ? (
        <p className="text-xs text-slate-500 mt-1 leading-relaxed line-clamp-2">
          {profile.description}
        </p>
      ) : null}
      {enabledBlocks.length > 0 ? (
        <p className="text-[11px] text-slate-400 mt-2 tabular-nums">
          {enabledBlocks.length} section{enabledBlocks.length === 1 ? "" : "s"}
        </p>
      ) : null}
    </>
  );

  const renderProfileListItem = (profile) => {
    const enabledBlocks = (profile.blocks || [])
      .slice()
      .sort((a, b) => a.order - b.order)
      .filter((b) => b.enabled !== false);

    return (
      <article
        key={profile._id || profile.profile_id}
        className="group flex items-center gap-4 bg-white border border-slate-200/70 rounded-xl px-4 py-3.5 hover:border-[#004f73]/25 hover:shadow-sm transition-all"
      >
        <div
          className={`w-1 self-stretch rounded-full shrink-0 ${
            profile.is_team_base ? "bg-violet-400" : "bg-[#004f73]"
          }`}
          aria-hidden
        />
        <div className="flex-1 min-w-0">{profileMeta(profile, enabledBlocks)}</div>
        {enabledBlocks.length > 0 ? (
          <div className="hidden md:flex flex-wrap gap-1 max-w-md justify-end">
            {enabledBlocks.slice(0, 4).map((b, i) => (
              <span
                key={b.block_id}
                className="text-[10px] px-2 py-0.5 rounded-md bg-slate-50 text-slate-500 ring-1 ring-slate-200/50"
              >
                {catalogMap[b.block_id] || b.block_id.replace(/_/g, " ")}
              </span>
            ))}
            {enabledBlocks.length > 4 ? (
              <span className="text-[10px] px-2 py-0.5 text-slate-400">
                +{enabledBlocks.length - 4}
              </span>
            ) : null}
          </div>
        ) : null}
        {profileActions(profile)}
      </article>
    );
  };

  const renderProfileTile = (profile) => {
    const enabledBlocks = (profile.blocks || [])
      .slice()
      .sort((a, b) => a.order - b.order)
      .filter((b) => b.enabled !== false);

    return (
      <article
        key={profile._id || profile.profile_id}
        className="group flex flex-col bg-white border border-slate-200/70 rounded-xl overflow-hidden hover:border-[#004f73]/25 hover:shadow-md transition-all"
      >
        <div
          className={`h-1 shrink-0 ${profile.is_team_base ? "bg-violet-400" : "bg-[#004f73]"}`}
          aria-hidden
        />
        <div className="flex flex-col flex-1 p-4">
          {profileMeta(profile, enabledBlocks)}
          {enabledBlocks.length > 0 ? (
            <ul className="mt-3 space-y-1 flex-1">
              {enabledBlocks.slice(0, 5).map((b, i) => (
                <li
                  key={b.block_id}
                  className="text-[11px] text-slate-500 flex items-center gap-2 truncate"
                >
                  <span className="text-slate-300 tabular-nums w-3 shrink-0">{i + 1}</span>
                  <span className="truncate">
                    {catalogMap[b.block_id] || b.block_id.replace(/_/g, " ")}
                  </span>
                </li>
              ))}
              {enabledBlocks.length > 5 ? (
                <li className="text-[10px] text-slate-400 pl-5">
                  +{enabledBlocks.length - 5} more
                </li>
              ) : null}
            </ul>
          ) : (
            <div className="flex-1" />
          )}
          <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-end">
            {profileActions(profile)}
          </div>
        </div>
      </article>
    );
  };

  return (
    <div className={embedded ? "nexus-surface-embed" : "min-h-full w-full bg-white"}>
      {showEditor && embedded ? (
        <div className="report-profile-editor-host w-full">
          <ProfileEditorModal
            profile={editingProfile}
            catalog={catalog}
            onSave={handleSaved}
            onClose={() => setShowEditor(false)}
            reportBase={editorReportBase}
            embedded
          />
        </div>
      ) : (
      <div className={embedded ? "w-full" : "w-full px-6 py-6"}>
        {!embedded ? (
        <>
        {/* Page header */}
        <header className="mb-6">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-[#004f73]/70 mb-2">
            Admin
          </p>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <h1 className="text-2xl font-bold text-gray-900">Report Profiles</h1>
            <div className="flex flex-wrap items-center gap-2">
              <ReportBaseToggle value={reportBase} onChange={setReportBase} />
              {!loading ? (
                <ViewModeToggle value={viewMode} onChange={handleViewModeChange} />
              ) : null}
              {!loading ? (
                <button
                  type="button"
                  onClick={() => handleNew(reportBase)}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#004f73] hover:bg-[#003a57] rounded-lg transition-colors"
                >
                  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4" aria-hidden>
                    <path
                      fillRule="evenodd"
                      d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  New profile
                </button>
              ) : null}
            </div>
          </div>
        </header>
        </>
        ) : (
        <div className="mb-4 flex flex-wrap items-center justify-end gap-2">
          <ReportBaseToggle value={reportBase} onChange={setReportBase} />
          {!loading ? <ViewModeToggle value={viewMode} onChange={handleViewModeChange} /> : null}
          {!loading ? (
            <button
              type="button"
              onClick={() => handleNew(reportBase)}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#004f73] hover:bg-[#003a57] rounded-lg transition-colors"
            >
              New profile
            </button>
          ) : null}
        </div>
        )}

        {/* Block catalog — collapsed by default */}
        {visibleCatalog.length > 0 && (
          <section className="report-profile-available-blocks mb-6 rounded-xl border border-gray-200 bg-gradient-to-b from-slate-50/80 to-white overflow-hidden">
            <button
              type="button"
              onClick={() => setBlocksExpanded((v) => !v)}
              aria-expanded={blocksExpanded}
              className={`w-full flex items-center justify-between gap-3 px-4 py-3 bg-white/80 text-left hover:bg-slate-50/80 transition-colors ${
                blocksExpanded ? "border-b border-gray-100" : ""
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#004f73]/10 text-[#004f73] shrink-0">
                  <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5" aria-hidden>
                    <path d="M2 4.5A2.5 2.5 0 014.5 2h2.879a2.5 2.5 0 011.732.7l1.06 1.06A1.5 1.5 0 0011.121 4.5H15.5A2.5 2.5 0 0118 7v8.5a2.5 2.5 0 01-2.5 2.5h-11A2.5 2.5 0 012 15.5v-11z" />
                  </svg>
                </span>
                <h2 className="text-sm font-semibold text-gray-900">Available blocks</h2>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs font-medium tabular-nums px-2.5 py-1 rounded-full bg-gray-100 text-gray-600">
                  {visibleCatalog.length}
                </span>
                <svg
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                    blocksExpanded ? "rotate-180" : ""
                  }`}
                  aria-hidden
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </button>
            {blocksExpanded ? (
              <div className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
                {visibleCatalog.map((b, i) => (
                  <div
                    key={b.block_id}
                    className="flex items-start gap-2 rounded-lg border border-gray-100 bg-white px-3 py-2.5 shadow-sm hover:border-[#004f73]/20 hover:shadow transition-colors min-w-0"
                    title={b.description || b.name}
                  >
                    <span className="text-[10px] font-semibold text-gray-300 tabular-nums pt-0.5 shrink-0">
                      {i + 1}
                    </span>
                    <span className="text-xs font-medium text-gray-700 leading-snug line-clamp-2">
                      {b.name}
                    </span>
                  </div>
                ))}
              </div>
            ) : null}
          </section>
        )}

        {/* Profiles list */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3">
            <Spinner className="h-8 w-8 text-[#004f73]" />
            <p className="text-sm text-slate-400">Loading profiles…</p>
          </div>
        ) : activeProfiles.length === 0 ? (
          <div className="text-center py-20 px-6 rounded-2xl border border-dashed border-slate-200 bg-white/60">
            <div className="mx-auto w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                className="w-6 h-6 text-slate-400"
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                />
              </svg>
            </div>
            <p className="text-base font-medium text-slate-700">No {reportBase} profiles yet</p>
            <button
              type="button"
              onClick={() => handleNew(reportBase)}
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-[#004f73] hover:bg-[#003a57] rounded-xl shadow-sm transition-colors"
            >
              Create {reportBase} profile
            </button>
          </div>
        ) : viewMode === "tiles" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {activeProfiles.map(renderProfileTile)}
          </div>
        ) : (
          <div className="space-y-2">
            {activeProfiles.map(renderProfileListItem)}
          </div>
        )}
      </div>
      )}

      {/* Standalone / non-Studio: viewport modal (portaled) */}
      {showEditor && !embedded ? (
        <ProfileEditorModal
          profile={editingProfile}
          catalog={catalog}
          onSave={handleSaved}
          onClose={() => setShowEditor(false)}
          reportBase={editorReportBase}
        />
      ) : null}
    </div>
  );
}
